import React from 'react';
import {
  Avatar,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Employee } from '../../types';

type EmployeePickerProps = {
  employees: Employee[];
  value: string;
  onChange: (employeeId: string) => void;
  label?: string;
  placeholder?: string;
  includeAllOption?: boolean;
  allOptionValue?: string;
  allOptionLabel?: string;
  profilePictures?: Record<string, string>;
  sx?: SxProps<Theme>;
};

const getEmployeeInitials = (employee: Employee) =>
  `${employee.firstName?.[0] ?? ''}${employee.lastName?.[0] ?? ''}`.toUpperCase() || 'E';

const basePickerSx: SxProps<Theme> = {
  maxWidth: 320,
  width: '100%',
  bgcolor: 'background.paper',
  borderRadius: 1.5,
  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    minHeight: 36,
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: 'text.secondary' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.95rem' },
  '& .MuiSelect-select': { fontSize: '0.98rem' },
  '& .MuiSelect-icon': { color: 'text.secondary' },
};

const EmployeePicker: React.FC<EmployeePickerProps> = ({
  employees,
  value,
  onChange,
  label = 'Employee',
  placeholder = 'Select employee',
  includeAllOption = false,
  allOptionValue = 'ALL_EMPLOYEES',
  allOptionLabel = 'All Employees',
  profilePictures,
  sx,
}) => {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      SelectProps={{
        renderValue: (rawValue) => {
          if (!rawValue) {
            return (
              <Typography variant="body1" color="text.secondary">
                {placeholder}
              </Typography>
            );
          }
          const selectedId = rawValue as string;
          const isAll = selectedId === allOptionValue;
          const employee = employees.find((item) => item.id === selectedId);
          const avatarSrc = !isAll && employee ? profilePictures?.[employee.id] : undefined;
          const displayLabel = isAll
            ? allOptionLabel
            : employee
              ? `${employee.firstName} ${employee.lastName}`
              : '';

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {!isAll && employee && (
                <Avatar
                  src={avatarSrc}
                  sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.300', color: 'text.primary' }}
                >
                  {getEmployeeInitials(employee)}
                </Avatar>
              )}
              <Typography variant="body1" fontWeight={600}>
                {displayLabel}
              </Typography>
            </Stack>
          );
        },
      }}
      sx={[basePickerSx, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {includeAllOption && (
        <MenuItem value={allOptionValue}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography variant="body1" fontWeight={600}>
              {allOptionLabel}
            </Typography>
          </Stack>
        </MenuItem>
      )}
      {employees.map((employee) => (
        <MenuItem key={employee.id} value={employee.id}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              src={profilePictures?.[employee.id]}
              sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'grey.300', color: 'text.primary' }}
            >
              {getEmployeeInitials(employee)}
            </Avatar>
            <Typography variant="body1" fontWeight={600}>
              {employee.firstName} {employee.lastName}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
};

export default EmployeePicker;
